/**
 * Loading indicators with rotating status messages that walk the user through
 * what the pipeline is doing.
 *
 * - AiExecutionLoader (default): spinner + rotating message. Used in the card
 *   dialogs (execute / edit) and on the dashboard when the output style changes.
 * - RotatingStatusMessage (named): message line only — for placing under an
 *   existing loader (dashboard load, filter apply).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Fade, Typography } from '@mui/material';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';

const ACCENT = 'rgb(64, 121, 141)';
const ROTATE_MS = 3200;
const FADE_MS = 250;

const EXECUTE_MESSAGES = [
  'Retrieving all survey responses for analysis…',
  'Analyzing the full dataset — every response is included…',
  'Calculating averages, medians and response distributions…',
  'Identifying locations, facilities and departments in the data…',
  'Reviewing written comments for recurring themes…',
  'Comparing segments to identify strengths and problem areas…',
  'Generating insights from the observed patterns…',
  'Validating figures against the underlying data…',
  'Structuring the findings into concise points…',
  'Finalizing the analysis for your dashboard…',
];

const RESTYLE_MESSAGES = [
  'Applying the selected output style…',
  'Re-running the analysis with the updated style…',
  'Adjusting the level of detail in each point…',
  'Reviewing the insights for the requested tone…',
  'Finalizing the restyled results…',
];

const DASHBOARD_MESSAGES = [
  'Fetching widgets and saved filters…',
  'Loading chart and KPI data…',
  'Preparing the AI insight cards…',
  'Arranging cards in the saved layout…',
  'Checking for saved preferences and output styles…',
  'Large surveys can take a few moments — thank you for your patience…',
];

const FILTER_MESSAGES = [
  'Applying the selected filters to the survey data…',
  'Re-querying charts with the filtered dataset…',
  'Re-running AI insight cards on the filtered responses…',
  'Recalculating figures for the selected segments…',
  'Finalizing the filtered view…',
];

const MESSAGE_SETS = {
  execute: EXECUTE_MESSAGES,
  restyle: RESTYLE_MESSAGES,
  dashboard: DASHBOARD_MESSAGES,
  filters: FILTER_MESSAGES,
};

/** Rotating status line only (no spinner) — place under an existing loader. */
export const RotatingStatusMessage = ({ variant = 'execute', sx = {} }) => {
  const messages = MESSAGE_SETS[variant] || EXECUTE_MESSAGES;
  const [index, setIndex] = useState(() => Math.floor(Math.random() * messages.length));
  const [visible, setVisible] = useState(true);
  const fadeTimerRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      fadeTimerRef.current = setTimeout(() => {
        setIndex((i) => (i + 1) % messages.length);
        setVisible(true);
      }, FADE_MS);
    }, ROTATE_MS);
    return () => {
      clearInterval(interval);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, [messages.length]);

  return (
    <Fade in={visible} timeout={FADE_MS}>
      <Typography
        variant="caption"
        aria-live="polite"
        sx={{
          display: 'block',
          color: 'text.secondary',
          textAlign: 'center',
          fontFamily: FONT_FAMILY_PRIMARY,
          minHeight: '1.2em',
          ...sx,
        }}
      >
        {messages[index]}
      </Typography>
    </Fade>
  );
};

const AiExecutionLoader = ({
  variant = 'execute',
  label = null,
  size = 28,
  minHeight = 120,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.75,
      minHeight,
      py: 2,
      px: 2,
    }}
  >
    <CircularProgress size={size} sx={{ color: ACCENT }} />
    {label && (
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: 'text.secondary', fontFamily: FONT_FAMILY_PRIMARY }}
      >
        {label}
      </Typography>
    )}
    <RotatingStatusMessage variant={variant} />
  </Box>
);

export default AiExecutionLoader;
